import { Trans } from "@lingui/react/macro";
import type { SubmitEvent } from "react";

import { Button } from "@/shared/ui/button";

import { useCurrentUser } from "../api/useCurrentUser";
import { readCsrfToken } from "../lib/readCsrfToken";
import { markSteamLogin } from "../lib/steamLogin";

// Адрес начала входа social-auth. Принимает только POST с CSRF-токеном, на GET отвечает 405.
const STEAM_LOGIN_URL = "/auth/login/steam/";

type LoginButtonProps = {
  // Путь, на который вернуть игрока после входа (передаётся social-auth как `next`).
  next?: string | undefined;
  // Код приглашения из ссылки друга: social-auth хранит его в сессии до возврата из Steam.
  referralCode?: string | undefined;
};

// Вход через Steam — обычная HTML-форма: браузер уходит на сайт Steam и возвращается уже с сессией.
// Cookie `csrftoken` выдаёт `GET /api/me` (у гостя — 401), поэтому кнопка ждёт этот запрос.
// Токен читается в момент отправки: cookie могла появиться после первого рендера. Вкладка
// получает отметку входа, чтобы возврат со Steam не посчитался заходом со Steam.
export const LoginButton = ({ next, referralCode }: LoginButtonProps) => {
  const { isPending } = useCurrentUser();

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    const field = event.currentTarget.elements.namedItem("csrfmiddlewaretoken");
    if (field instanceof HTMLInputElement) {
      field.value = readCsrfToken();
    }
    markSteamLogin();
  };

  return (
    <form method="post" action={STEAM_LOGIN_URL} onSubmit={submit}>
      <input type="hidden" name="csrfmiddlewaretoken" defaultValue="" />
      {next !== undefined && <input type="hidden" name="next" value={next} />}
      {referralCode !== undefined && <input type="hidden" name="ref" value={referralCode} />}
      <Button
        type="submit"
        variant="outline"
        disabled={isPending}
        className="h-13 w-full rounded-lg border-steam-border bg-steam text-base font-semibold text-steam-foreground hover:bg-steam hover:brightness-125"
      >
        <Trans>Sign in with Steam</Trans>
      </Button>
    </form>
  );
};
